import React from 'react';
import { Box, Button, TextField } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import AddIcon from '@mui/icons-material/Add';

import {
  BORDER, BORDER_HOVER, DIVIDER, MUTED, SANS_STACK, SUCCESS, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';

const NewListRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 16px',
  cursor: 'pointer',
  borderBottom: `1px solid ${DIVIDER}`,
  transition: 'background-color 0.1s',
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
  flexShrink: 0,
}));

const PlusTile = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { fontSize: 16 },
}));

const NewListLabel = styled('div')(({ theme }) => ({
  fontSize: 13.5,
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const InlineCreateWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '8px 16px 12px',
  borderBottom: `1px solid ${DIVIDER}`,
  flexShrink: 0,
});

const InlineCreateActions = styled(Box)({
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
});

const nameFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13.5,
    fontWeight: 500,
    fontFamily: SANS_STACK,
    color: TEXT_DARK,
    borderRadius: '8px',
    '& fieldset': { borderColor: BORDER, borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: BORDER_HOVER },
    '&.Mui-focused fieldset': { borderColor: SUCCESS, borderWidth: '1.5px' },
  },
  '& .MuiOutlinedInput-input': { padding: '8px 12px', height: 20 },
  '& .MuiOutlinedInput-input::placeholder': { color: MUTED, opacity: 1 },
};

/**
 * Creating a list without leaving the picker: a row that turns into a name
 * field. `open` is the caller's, since the row and the field occupy the same
 * slot and only one shows at a time.
 */
export default function CreateListForm({
  open, name, creating, inputRef, onOpen, onNameChange, onSubmit, onCancel,
}) {
  const theme = useTheme();

  if (!open) {
    return (
      <NewListRow onClick={onOpen}>
        <PlusTile><AddIcon /></PlusTile>
        <NewListLabel>New list</NewListLabel>
      </NewListRow>
    );
  }

  return (
    <InlineCreateWrap>
      <TextField
        inputRef={inputRef}
        autoFocus
        variant="outlined"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="List name"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          } else if (e.key === 'Escape') {
            onCancel();
          }
        }}
        sx={nameFieldSx}
      />
      <InlineCreateActions>
        <Button
          size="small"
          onClick={onCancel}
          sx={{ textTransform: 'none', fontSize: 13, color: TEXT_SECONDARY }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={onSubmit}
          disabled={!name.trim() || creating}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {creating ? 'Creating…' : 'Create'}
        </Button>
      </InlineCreateActions>
    </InlineCreateWrap>
  );
}
